import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Lock, ShieldCheck, Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';

/* Reset Password
   Accepts ?token=...&email=... from the email link, lets the user set a
   new password, then bounces them to /login with a success toast. */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const token = params.get('token') || '';
  const email = params.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setError('This reset link is incomplete or invalid. Request a new one.');
    }
  }, [token, email]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');

    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, email, password });
      setDone(true);
      toast.success('Password updated. Logging in…');
      // Clear any stale auth so login is clean
      try { localStorage.removeItem('token'); } catch (_e) { /* noop */ }
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not reset password. The link may have expired.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: '#0a0b0f' }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/20 mb-4">
            <ShieldCheck className="text-teal-400" size={26} />
          </div>
          <h1 className="text-3xl font-semibold text-white">Set a new password</h1>
          <p className="text-sm text-slate-400 mt-2">
            For <span className="text-teal-300">{email || 'your account'}</span>
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-8">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
                <CheckCircle2 className="text-emerald-300" size={22} />
              </div>
              <h2 className="text-xl font-semibold text-white">Password updated</h2>
              <p className="text-sm text-slate-400">Redirecting to login…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">New password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full pl-10 pr-10 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50"
                    data-testid="reset-password-input"
                    required
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Confirm password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-type the password"
                    className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50"
                    data-testid="reset-password-confirm"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-300" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token || !email}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-semibold hover:bg-teal-500 transition disabled:opacity-50"
                data-testid="reset-password-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                Update password
              </button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
                  <ArrowLeft size={13} /> Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
